import { escapeAttr, escapeHtml } from '../pasteUtils/htmlEscape';
import {
  ARRAY_INDEX_ALL,
  formatVariableRef,
  isArrayAllIndex,
  parseVariableRef,
} from '../../../../utils/variableScope';

/** Fresh pill markup for inserting a reference to `name` / `name[1]` / `name[*]`.
 *  Default trailing ZWSP (not a visible space) so the caret has a real text
 *  node after the contenteditable=false pill — without it, paste/insertHTML
 *  at the flush boundary can select/unwrap the pill. */
export const buildVariableHtml = (name, { index = null, trailingSpace = false } = {}) => {
  const parsed = parseVariableRef(name);
  const base = parsed.name;
  const idx =
    isArrayAllIndex(index) || isArrayAllIndex(parsed.index)
      ? ARRAY_INDEX_ALL
      : index != null && index >= 1
        ? index
        : parsed.index;
  const label = formatVariableRef(base, idx) || base;
  const indexAttr = isArrayAllIndex(idx)
    ? ` data-var-index="${ARRAY_INDEX_ALL}"`
    : idx != null && idx >= 1
      ? ` data-var-index="${escapeAttr(String(idx))}"`
      : '';
  return (
    `<span class="doc-variable" data-doc-variable data-var-name="${escapeAttr(base)}"${indexAttr} ` +
    `data-var-state="unresolved" contenteditable="false" spellcheck="false">${escapeHtml(label)}</span>` +
    (trailingSpace ? '&nbsp;' : '\u200B')
  );
};

/** Normalize a typed/pasted {{name}} / {{name[1]}} token into a base name. */
export const normalizePastedVariableName = (raw) => parseVariableRef(raw).name;

/** Full ref parse for paste/convert paths (`name` + optional 1-based index). */
export const normalizePastedVariableRef = (raw) => parseVariableRef(raw);

/** Canonical `name` / `name[1]` / `name[*]` from a pill element's data attrs. */
export const pillRefFromTag = (tag) => {
  const name = tag.getAttribute('data-var-name') || '';
  const rawIndex = tag.getAttribute('data-var-index');
  if (rawIndex === ARRAY_INDEX_ALL || rawIndex === '*') {
    return formatVariableRef(name, ARRAY_INDEX_ALL);
  }
  const index = rawIndex != null && rawIndex !== '' ? parseInt(rawIndex, 10) : null;
  return formatVariableRef(name, Number.isFinite(index) && index >= 1 ? index : null);
};

/** `{{name}}` tokens in plain text (code blocks, nested values). */
export const BRACE_VAR_RE = /\{\{\s*([^{}]+?)\s*\}\}/g;

/**
 * Replace `{{name}}` tokens in a plain string using `resolveVariable(name)`
 * → `{ value } | null`. Resolved non-empty values are substituted; unknown
 * or empty variables are omitted (same as empty pills on copy — invisible).
 * Used by code / callout Copy — pills use HTML, not this path.
 */
export const expandBraceVarsInText = (text, resolveVariable) => {
  const source = String(text ?? '');
  if (!source.includes('{{') || !resolveVariable) return source;
  return source.replace(BRACE_VAR_RE, (match, rawName) => {
    const { name, ref } = normalizePastedVariableRef(rawName);
    if (!name) return match;
    const hit = resolveVariable(ref);
    if (!hit) return '';
    const value = String(hit.value ?? '');
    if (!value.trim()) return '';
    return value;
  });
};

/**
 * Turn literal `{{name}}` tokens in sanitized HTML into variable pills.
 * Walks text nodes only — never attributes — so `data-var-tip="{{name}} - …"`
 * on an existing pill can't be rewritten into nested tip garbage on paste.
 * Text already inside a pill is left alone. Each new pill gets a trailing
 * ZWSP caret buffer (see buildVariableHtml).
 */
export const convertBraceVarsToPills = (html) => {
  if (!html || !html.includes('{{')) return html;
  const host = document.createElement('div');
  host.innerHTML = html;
  const braceRe = /\{\{\s*([^{}]+?)\s*\}\}/g;
  const walker = document.createTreeWalker(host, NodeFilter.SHOW_TEXT);
  const textNodes = [];
  let node = walker.nextNode();
  while (node) {
    textNodes.push(node);
    node = walker.nextNode();
  }
  textNodes.forEach((textNode) => {
    if (textNode.parentElement?.closest?.('[data-doc-variable], [data-doc-math]')) return;
    const text = textNode.textContent || '';
    if (!text.includes('{{')) return;
    braceRe.lastIndex = 0;
    const frag = document.createDocumentFragment();
    let last = 0;
    let changed = false;
    let match;
    while ((match = braceRe.exec(text)) !== null) {
      const { name, index } = normalizePastedVariableRef(match[1]);
      if (match.index > last) {
        frag.appendChild(document.createTextNode(text.slice(last, match.index)));
      }
      if (name) {
        const holder = document.createElement('div');
        holder.innerHTML = buildVariableHtml(name, { index, trailingSpace: false });
        while (holder.firstChild) frag.appendChild(holder.firstChild);
        changed = true;
      } else {
        frag.appendChild(document.createTextNode(match[0]));
      }
      last = match.index + match[0].length;
    }
    if (!changed) return;
    if (last < text.length) {
      frag.appendChild(document.createTextNode(text.slice(last)));
    }
    textNode.parentNode.replaceChild(frag, textNode);
  });
  return host.innerHTML;
};
